import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ClearableDirective,
  FieldComponent,
  Icon,
  SelectComponent,
  SelectSelection,
} from '@circlek-global/lds-components';

import { GtmCustomEventNameEnum } from '~components/gtm-script/gtm-custom-event-name.enum';
import { GtmCustomEventService } from '~components/gtm-script/gtm-custom-event.service';
import { StationsFilterButtonComponent } from '~components/stations-filters/stations-filters-button/stations-filters-button.component';
import { TranslatePipe } from '~pipes/translate.pipe';
import { AutocompleteService } from '~services/autocomplete.service';
import { BusinessUnit } from '~shared/business-unit';
import { isSelectComplexItem } from '~utils/lds-select-utility';
import { sentryError } from '~utils/sentry-error';

@Component({
  selector: 'app-station-search',
  imports: [
    FieldComponent,
    FormsModule,
    ReactiveFormsModule,
    SelectComponent,
    ClearableDirective,
    StationsFilterButtonComponent,
    TranslatePipe,
  ],
  templateUrl: './station-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StationSearchComponent {
  private readonly gtmCustomEventService = inject(GtmCustomEventService);
  private readonly autocompleteService = inject(AutocompleteService);
  readonly bu = input.required<BusinessUnit>();
  protected readonly select = this.autocompleteService.autocompleteSelect;
  protected readonly suggestions = computed(() => [
    this.autocompleteService.NEAREST_SUGGESTION,
    ...this.autocompleteService.suggestions(),
  ]);
  protected readonly Icon = Icon;

  onSearch(search: string): void {
    if (search) {
      this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.FindStationSearchbar, { typed_value: search });
    }

    this.autocompleteService.search$.next(search);
  }

  onSuggestionSelected(suggestion: SelectSelection): void {
    if (suggestion == null) {
      return;
    }

    if (!isSelectComplexItem(suggestion) || typeof suggestion.id !== 'string') {
      sentryError('onSuggestionSelected Error', 'Suggestion Select Error', 'Selected item was not SelectComplexItem', {
        suggestion,
      });

      return;
    }

    if (suggestion.id === this.autocompleteService.NEAREST_SUGGESTION.id) {
      this.autocompleteService.findNearestStation();

      return;
    }

    this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.FindStationSearchbar, {
      suggestion: `${suggestion.text}`,
    });

    this.autocompleteService.saveSuggestionToLocalStorage(suggestion);
    this.autocompleteService.navigateOnSuggestion(suggestion.id).subscribe();
  }
}
